import moment from 'moment';
import { useContext, useMemo } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from 'react-query';
import { useParams, useRouteMatch } from 'react-router-dom';

import { AuthContext } from '../../contexts/authContext';
import carrierService from '../../services/carrierService';

export const useCarriers = (disabled) => {
  const { data, isLoading, status } = useQuery(
    'allCarriers',
    () => carrierService.getAll(),
    {
      enabled: !disabled,
    }
  );

  const carriers = data || [];
  return {
    allCarriers: carriers.filter((c) => !c.isDeleted),
    inactiveCarriers: carriers.filter((c) => c.isDeleted),
    isLoading,
    status,
  };
};

export const useCarriersList = (enabled) => {
  const { data, status } = useQuery(
    'carriersList',
    () => carrierService.getList(),
    {
      enabled: !!enabled,
    }
  );

  return {
    carriersList: (data || []).filter((c) => !c.isDeleted),
    status,
  };
};

export const useCarrierOrders = (carrier) => {
  const { data, error, isLoading } = useQuery('carrierOrdersList', () =>
    carrierService.getCrmCarrierOrders(carrier)
  );

  if (error) {
    return {
      carrierOrdersList: [],
      error,
      isLoading: false,
    };
  }

  return {
    carrierOrdersList: data || [],
    error: null,
    isLoading,
  };
};

export const useSelectedCarrier = () => {
  const { authUser } = useContext(AuthContext);
  const { carrierId } = useParams();
  const carrierMatch = useRouteMatch('/carrier/:carrierId');
  const carrierIdFromRoute = carrierMatch?.params?.carrierId;
  const resolvedCarrierId = carrierId ?? carrierIdFromRoute;

  const currentCarrierId = Number(
    authUser?.carrierId ? authUser?.carrierId : resolvedCarrierId
  );

  const { data: selectedCarrier, status } = useQuery(
    ['carrier', currentCarrierId],
    () => carrierService.get(currentCarrierId),
    {
      enabled: !!currentCarrierId,
      retry: 0,
    }
  );

  return {
    carrierId: currentCarrierId,
    selectedCarrier,
    status,
  };
};

export const useCarrier = (carrierId) => {
  const { data, isLoading, status } = useQuery(['carrier', carrierId], () =>
    carrierService.get(carrierId)
  );

  return {
    carrier: data,
    isLoading,
    status,
  };
};

export const useCarrierCreate = () => {
  const queryClient = useQueryClient();

  return useMutation(carrierService.create, {
    onSuccess: (data) => {
      queryClient.setQueryData('allCarriers', (carriers) => [
        ...(carriers || []),
        data,
      ]);

      queryClient.setQueryData('carriersList', (carriers) => [
        ...(carriers || []),
        data,
      ]);
    },
  });
};

export const useCarrierUpdate = () => {
  const queryClient = useQueryClient();

  return useMutation(carrierService.update, {
    onSuccess: (data) => {
      queryClient.setQueryData(
        'allCarriers',
        (carriers) =>
          carriers?.map((carrier) =>
            carrier.id === data.id ? { ...carrier, ...data } : carrier
          ) || []
      );

      queryClient.setQueryData(['carrier', data.id], (carrier) => data);

      queryClient.setQueryData('carriersList', (list) => {
        const filtered = (list || []).filter((c) => c.id !== data.id);
        return data.isDeleted ? filtered : [...filtered, data];
      });
    },
  });
};

export const useCarrierRemove = () => {
  const queryClient = useQueryClient();

  return useMutation(carrierService.remove, {
    onSuccess: (_data, deletedCarrierId) => {
      queryClient.setQueryData(
        'allCarriers',
        (carriers) =>
          carriers?.map((carrier) =>
            carrier.id === deletedCarrierId
              ? { ...carrier, isDeleted: true }
              : carrier
          ) || []
      );

      queryClient.setQueryData(
        'carriersList',
        (carriers) =>
          carriers?.filter((carrier) => carrier.id !== deletedCarrierId) || []
      );

      queryClient.setQueryData(['carrier', deletedCarrierId], (carrier) =>
        carrier ? { ...carrier, isDeleted: true } : carrier
      );
    },
  });
};

export const useCarrierTerminalMap = (disabled) => {
  const { data, status } = useQuery(
    'carrierTerminalMap',
    () => carrierService.getCarrierTerminalMap(),
    {
      enabled: !disabled,
    }
  );

  return {
    carrierTerminalMap: data || [],
    status,
  };
};

export const useCarrierTerminalMapUpdate = () => {
  const queryClient = useQueryClient();

  return useMutation(carrierService.updateCarrierTerminalMap, {
    onSuccess: (data, variables) => {
      queryClient.setQueryData('carrierTerminalMap', () => data);
    },
  });
};

export const useCarrierNotes = (carrierId) => {
  const { data, status } = useQuery(
    ['carrierNotes', carrierId],
    () => carrierService.getNotes(carrierId),
    { enabled: !!carrierId }
  );

  return {
    carrierNotes: data || [],
    status,
  };
};

export const useCarrierOption = (carrierId) => {
  const { data, status } = useQuery(
    ['carrierOption', carrierId],
    () => carrierService.getOption(carrierId),
    { enabled: !!carrierId }
  );

  return {
    carrierOption: data ?? null,
    status,
  };
};

export const useCostKmStats = (carrierId, enabled = true) => {
  const { data, isFetching, isLoading, refetch, status } = useQuery(
    ['costKmStats', carrierId],
    () => carrierService.getCostKmStats(carrierId),
    {
      enabled: !!carrierId && enabled,
      staleTime: 5 * 60 * 1000,
    }
  );

  return {
    costKmStats: data || [],
    isFetching,
    isLoading,
    refetch,
    status,
  };
};

export const useCreateCarrierNote = () => {
  const queryClient = useQueryClient();

  return useMutation(carrierService.createNote, {
    onSuccess: (data) => {
      queryClient.setQueryData(['carrierNotes', data.carrierId], (notes) => [
        data,
        ...(notes || []),
      ]);
      queryClient.setQueryData(['carrier', data.carrierId], (carrier) =>
        carrier
          ? { ...carrier, notes: [data, ...(carrier.notes || [])] }
          : carrier
      );
    },
  });
};

export const useDeleteCarrierNote = () => {
  const queryClient = useQueryClient();

  return useMutation(carrierService.deleteNote, {
    onSuccess: (_data, deletedNoteId) => {
      queryClient.invalidateQueries('carrierNotes');
    },
  });
};

const getDaysSinceExpiry = (fuelSurchargeExpiry) => {
  if (!fuelSurchargeExpiry) return null;
  const expiry = moment(fuelSurchargeExpiry).startOf('day');
  if (!expiry.isValid()) return null;
  return moment().startOf('day').diff(expiry, 'days');
};

export const useExpiringSurcharges = (enabled = true) => {
  const { allCarriers } = useCarriers();

  const optionQueries = useQueries(
    allCarriers.map((carrier) => ({
      enabled: !!carrier.id && enabled,
      queryFn: () => carrierService.getOption(carrier.id),
      queryKey: ['carrierOption', carrier.id],
    }))
  );

  return useMemo(() => {
    const critical = [];
    const warning = [];
    const soonToExpire = [];

    allCarriers.forEach((carrier, index) => {
      const option = optionQueries[index]?.data;
      const fuelSurchargeExpiry = option?.fuelSurchargeExpiry;
      const daysSince = getDaysSinceExpiry(fuelSurchargeExpiry);
      if (daysSince == null || daysSince < -3) return;

      const expiryFormatted = moment(fuelSurchargeExpiry).format('YYYY-MM-DD');
      const expiredLabel = `${expiryFormatted}`;

      const item = {
        carrier,
        daysSince,
        expiredLabel,
      };

      if (daysSince < 0) {
        soonToExpire.push(item);
      } else if (daysSince > 7) {
        critical.push(item);
      } else {
        warning.push(item);
      }
    });

    critical.sort((a, b) => b.daysSince - a.daysSince);
    warning.sort((a, b) => a.daysSince - b.daysSince);
    soonToExpire.sort((a, b) => b.daysSince - a.daysSince);

    return {
      critical,
      expiring: [...critical, ...warning, ...soonToExpire],
      isLoading: optionQueries.some((q) => q.isLoading),
      soonToExpire,
      warning,
    };
  }, [allCarriers, optionQueries]);
};
